import React from 'react';
import { I18n } from 'react-redux-i18n';
import { Check, AlertCircle } from '@untitledui/icons';

import { I18N_BASE } from '../../constants';
import { BodyHashClusterItem, FanOutResult } from '../../models';
import { clusterItemLabel } from '../../helpers/clusterItemLabel';
import CmCheckbox from '../../components/primitives/CmCheckbox';
import CmLoader from '../../components/primitives/CmLoader';
import ScorePill from '../../components/ScorePill/ScorePill';
import styles from './modals.module.css';

export interface BodyHashDuplicatesListProps {
  items: BodyHashClusterItem[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  fanOutResults: Record<number, FanOutResult>;
  disabled?: boolean;
  testId?: string;
}

function StatusIndicator({ result }: { result: FanOutResult | undefined }): React.ReactElement | null {
  if (!result) return null;
  if (result.status === 'submitting') {
    return (
      <span className={`${styles.duplicateStatus} ${styles.statusSubmitting}`}>
        <CmLoader size="tiny" />
        {I18n.t(`${I18N_BASE}.blockModal.fanOut.submitting`)}
      </span>
    );
  }
  if (result.status === 'success') {
    return (
      <span className={`${styles.duplicateStatus} ${styles.statusSuccess}`}>
        <Check width={14} height={14} />
        {I18n.t(`${I18N_BASE}.blockModal.fanOut.success`)}
      </span>
    );
  }
  if (result.status === 'failure') {
    return (
      <span
        className={`${styles.duplicateStatus} ${styles.statusFailure}`}
        title={result.errorCode}
      >
        <AlertCircle width={14} height={14} />
        {I18n.t(`${I18N_BASE}.blockModal.fanOut.failure`)}
      </span>
    );
  }
  return null;
}

export function BodyHashDuplicatesList({
  items,
  selectedIds,
  onToggle,
  fanOutResults,
  disabled = false,
  testId,
}: BodyHashDuplicatesListProps): React.ReactElement | null {
  if (items.length === 0) return null;

  return (
    <div className={styles.section} data-testid={testId}>
      <div className={styles.sectionTitle}>
        {I18n.t(`${I18N_BASE}.blockModal.duplicates.title`, { count: items.length })}
      </div>
      {items.map((item) => {
        const checked = selectedIds.includes(item.contentModerationEventId);
        const result = fanOutResults[item.contentModerationEventId];
        const rowDisabled = disabled || result?.status === 'success';
        return (
          <div className={styles.duplicateRow} key={item.contentModerationEventId}>
            <div className={styles.duplicateLeft}>
              <CmCheckbox
                checked={checked}
                onChange={() => onToggle(item.contentModerationEventId)}
                disabled={rowDisabled}
                testId={`duplicate-checkbox-${item.contentModerationEventId}`}
                ariaLabel={I18n.t(`${I18N_BASE}.general.selectDuplicateAriaLabel`, {
                  id: item.contentModerationEventId,
                })}
              />
              <span>{clusterItemLabel(item)}</span>
              <ScorePill score={item.aiScore} size="small" />
            </div>
            <StatusIndicator result={result} />
          </div>
        );
      })}
    </div>
  );
}

export default BodyHashDuplicatesList;
